/**
 * The open-lane and filing surfaces of the AI Imports pages, as pure functions.
 *
 * The app must never promise what the server refuses, so every rule here that decides whether a button
 * shows is a COPY of the server's rule, kept in one place where a test can hold the two side by side:
 *   - Send decline       <- send-ai-import-report's `send_decline` branch: decideStaffDecline (report-send.ts)
 *                           for the job, then what sendExternalEmail (external-ack.ts) refuses on before it looks
 *                           anything up: the recipient (recipientRefusal) and the inbound's header facts;
 *   - Retry filing       <- ai-import-file's `retry` action: isFileable / isRequeueable / claimDecision
 *                           (_shared/ai-import-filing.ts);
 *   - Treat as property  <- the poller's override pass (runOpenLaneOverrides): declined, capped and dropped leads,
 *                           and only while tier2Route says 'analyze' (unknown senders accepted, "Triage and analyze").
 * tests/open-lane-ui.test.ts runs each copy against the server function itself over a grid of inputs, so a
 * rule that changes on the server fails there.
 *
 * Everything a stranger wrote (a subject, a display name, the triage model's reason, an error that echoes a
 * file name) reaches the page as React text, cleaned here the way the report email cleans it
 * (cleanDisplayText); links come only from safeExternalUrl. Nothing in this file produces HTML.
 */

import type { AIImportEmailMessage, AIImportJob, AIImportJobFileFiling, OpenLaneLead } from '@/types/ai-import';
import { shouldAcknowledge } from '../../supabase/functions/_shared/relationship-manager';

// ---------------------------------------------------------------------------------------------
// Text a stranger shaped, and links
// ---------------------------------------------------------------------------------------------

/**
 * Text a sender can shape, made safe to read. React escapes it, but escaping does not stop a right-to-left
 * override from reordering what follows (invoice<RLO>fdp.exe reads "invoiceexe.pdf"). So control, surrogate
 * and line/paragraph separator characters become spaces, invisible format characters (zero-width, bidi) are
 * deleted, and whitespace is collapsed. Letters of any script, accents and emoji survive. This is the report
 * email's own cleaning (_shared/email-files-section.ts), so the app and the email show a stranger alike.
 */
export function cleanDisplayText(raw: string | null | undefined): string {
  return String(raw ?? '')
    .replace(/[\p{Cc}\p{Cs}\p{Zl}\p{Zp}]/gu, ' ')
    .replace(/\p{Cf}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A URL that may become a link's href: an absolute https URL with no credentials in it, normalized, or null.
 * The filed-copy URL is one our own code stored, but it lands in an href, so only https ever does. Never
 * javascript:, data:, http:, a relative path, or `https://trusted.host@evil.host/`.
 */
export function safeExternalUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text) return null;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null;
  return url.href;
}

// ---------------------------------------------------------------------------------------------
// The import detail's open-lane banner
// ---------------------------------------------------------------------------------------------

type SenderFields = Pick<AIImportJob, 'source_email_sender' | 'submitted_by_email' | 'original_sender_name'>;

/**
 * Who sent the email, as the report's open-lane banner names them. On a re-run `submitted_by` is the teammate
 * who re-ran it, so it is never used: the name is `original_sender_name` (the stranger as they named
 * themselves) falling back to the address, and the address is `source_email_sender` (then the submitter's
 * address, only when the source email has none), exactly the fields send-ai-import-report reads.
 */
export function openLaneSender(job: SenderFields): { name: string; email: string } {
  const email = cleanDisplayText(job.source_email_sender ?? job.submitted_by_email ?? '');
  return { name: cleanDisplayText(job.original_sender_name) || email, email };
}

export interface TriageSummary {
  /** In words: "not a property", not the stored code. An unknown verdict is shown as it was written. */
  verdict: string;
  /** 0-100. */
  confidencePct: number;
  /** May be empty; the page then leaves its dash out. */
  reason: string;
}

const VERDICT_WORDS = new Map<string, string>([
  ['property', 'property'],
  ['not_property', 'not a property'],
  ['unsure', 'unsure'],
]);

/**
 * ai_import_jobs.triage is model output stored as jsonb, so its shape is a claim, not a fact. Returns the
 * summary only for a value that is an object with a string verdict, a string reason and a finite numeric
 * confidence; anything else is null (the page draws nothing) and never throws.
 */
export function readTriage(raw: unknown): TriageSummary | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.verdict !== 'string' || typeof t.reason !== 'string') return null;
  if (typeof t.confidence !== 'number' || !Number.isFinite(t.confidence)) return null;
  const verdict = cleanDisplayText(t.verdict);
  if (!verdict) return null;
  return {
    verdict: VERDICT_WORDS.get(verdict) ?? verdict,
    confidencePct: Math.round(Math.min(1, Math.max(0, t.confidence)) * 100),
    reason: cleanDisplayText(t.reason),
  };
}

const EXACT_ACK_REASONS = new Map<string, string>([
  ['no from address', 'the email had no sender address'],
  ['own domain', 'it came from one of our own domains'],
  ['mailing list', 'it came from a mailing list'],
  ['x-auto-response-suppress', 'the sender asked for no automatic replies'],
  ['empty return-path (bounce)', 'it looked like a bounce (empty return path)'],
  ['unknown', "the email's headers could not be checked"],
]);

const PARAMETRIC_ACK_REASONS: Array<[RegExp, (arg: string) => string]> = [
  [/^role\/bounce localpart \((.+)\)$/, (arg) => `it came from a system address (${arg})`],
  [/^auto-submitted: (.+)$/, (arg) => `it was marked as automatic mail (${arg})`],
  [/^precedence: (.+)$/, (arg) => `it was marked as bulk mail (${arg})`],
];

/**
 * Plain words for a loop-suppression code the poller wrote (ackGuards.ts loopSuppressionReason, and
 * wireHeaderFacts' fail-closed 'unknown'); a code this file does not know is shown as it is, cleaned. The
 * words finish a sentence after a dash: "No automatic reply was sent — it came from a mailing list."
 */
export function ackSuppressionText(code: string | null | undefined): string | null {
  const text = cleanDisplayText(code);
  if (!text) return null;
  const exact = EXACT_ACK_REASONS.get(text);
  if (exact) return exact;
  for (const [pattern, words] of PARAMETRIC_ACK_REASONS) {
    const match = pattern.exec(text);
    if (match) return words(match[1]);
  }
  return text;
}

/**
 * The banner's headline. "Nothing has been written to Flow" is true until a teammate applies the job, and
 * the open lane never applies one on its own, so it must not stay on a page whose job was applied.
 */
export function openLaneBannerHeadline(job: Pick<AIImportJob, 'status' | 'rolled_back_at'>): string {
  if (job.status === 'applied') return 'Unsolicited submission — a teammate applied it to Flow';
  if (job.rolled_back_at) return 'Unsolicited submission — applied, then rolled back';
  return 'Unsolicited submission — nothing has been written to Flow';
}

/**
 * What the sender was told, as far as this job's own row knows. A re-run's thank-you went out under the job
 * it re-runs (the report asks both), and this row cannot see that job's mail, so it makes no claim either way.
 */
export function senderToldText(
  job: Pick<AIImportJob, 'parent_job_id' | 'sender_ack_message_id' | 'ack_allowed' | 'ack_suppression_reason'>,
): string {
  if (job.sender_ack_message_id) return 'The sender received a branded thank-you.';
  if (job.parent_job_id) return 'This is a re-run: what the sender was told is recorded on the original submission.';
  const why = ackSuppressionText(job.ack_suppression_reason);
  if (why) return `No automatic reply was sent — ${why}.`;
  if (job.ack_allowed === false) return 'No automatic reply was sent — the inbound email was not DMARC-aligned.';
  return 'No automatic reply was sent.';
}

/** The two outcomes send-ai-import-report records on a plan, as the banner's badge names them. */
export function openLaneOutcomeLabel(plan: unknown): string | null {
  const outcome = (plan as { open_lane_outcome?: unknown } | null | undefined)?.open_lane_outcome;
  if (outcome === 'no_property') return 'No property found';
  if (outcome === 'declined_by_staff') return 'Declined by staff';
  return null;
}

/**
 * What the app says a sender outside the team added to a job by reply: the report email's own clause
 * (outsideContributionHtml, send-ai-import-report/report-send.ts; tests/open-lane-ui.test.ts holds the two to each
 * other). "Files or answers" because `external_contributions` is set by both: an outside sender's files, staged
 * onto the job, and their answers to its questions, recorded as proposals for a teammate to confirm (ai-import-reply
 * and the poller set it before any of it lands). Fixed words: nothing a sender wrote.
 */
export const OUTSIDE_CONTENT_CLAUSE = 'A sender outside the team added files or answers to this job by reply';

/**
 * What a teammate's job says once that has happened. The job is the teammate's, so it has no open-lane banner, but
 * content from outside the allowlist is on it (`external_contributions`) and the worker will not auto-apply it.
 * (Named for the files it first covered; the docs and tests know it by this name.)
 */
export const REPLY_FILES_NOTE = `${OUTSIDE_CONTENT_CLAUSE}: review them before applying. This job won't auto-apply.`;

/** After these nothing can be applied, so "review them before applying" would be an instruction with nothing to act on. */
const CLOSED_STATUSES: ReadonlySet<string> = new Set(['applied', 'failed', 'cancelled']);

/**
 * Does the detail page draw the reply-files banner? For a TRUSTED job with external contributions, until it can no
 * longer be applied. An open-lane job never does: its own banner already says when a sender outside the team added
 * files or answers, and a job whose lane the row does not say gets that banner too (isUnsolicitedJob reads it as
 * unsolicited). So the page draws one banner or the other for every job the bulk actions set aside
 * (hasUnsolicitedContent, lib/aiImportQueue), and never both. A status this page does not know shows it: the note is
 * informational, so it errs towards being said.
 */
export function showsReplyFilesBanner(job: Pick<AIImportJob, 'intake_lane' | 'external_contributions' | 'status'>): boolean {
  return job.intake_lane === 'trusted' && job.external_contributions === true && !CLOSED_STATUSES.has(job.status);
}

// ---------------------------------------------------------------------------------------------
// Send decline
// ---------------------------------------------------------------------------------------------

/** What the decline rule reads off the job row: the columns decideStaffDecline and the header facts read. */
export interface DeclineJob {
  intake_lane: string | null;
  /** Set on a re-run, which carries a teammate's reply ids and name rather than the stranger's. */
  parent_job_id: string | null;
  status: string | null;
  source_email_sender: string | null;
  /** The Graph id of the stranger's email: what every once-per-inbound-message rule keys on. */
  source_email_message_id: string | null;
  ack_allowed: boolean | null;
  ack_suppression_reason: string | null;
}

/**
 * The two constants external-ack.ts checks an address against before it asks shouldAcknowledge. They are not
 * exported there (that module needs Deno), so they are copied here, and tests/open-lane-ui.test.ts reads the
 * server's source to hold each copy to the original. STRICT_ADDRESS is a plain ASCII address: no display name, no
 * brackets, no whitespace, no `*`, no accented letter.
 */
export const STRICT_ADDRESS = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
export const MAX_ADDRESS_LENGTH = 254; // RFC 5321

/** Why an address is not one the decline may go to. */
export type RecipientRefusal = 'address' | 'automated' | 'internal';

/**
 * Would sendExternalEmail refuse this recipient, and why? It is the first thing the send checks (external-ack.ts
 * recipientRefusal): a plain ASCII address, then shouldAcknowledge (relationship-manager.ts), which turns away
 * no-reply, notification, automated, bounce and daemon senders and every address on our own domains, subdomains
 * included. shouldAcknowledge is the server's own function, imported: there is one list of those senders.
 */
export function recipientRefusal(to: unknown): RecipientRefusal | null {
  if (typeof to !== 'string' || to.length > MAX_ADDRESS_LENGTH || !STRICT_ADDRESS.test(to)) return 'address';
  const answer = shouldAcknowledge(to);
  if (answer.ok) return null;
  if (answer.reason === 'internal / team address') return 'internal';
  if (answer.reason === 'no-reply / automated sender') return 'automated';
  return 'address';
}

/** Why the server would refuse the decline, in the order it checks (its status in the comment). */
export type DeclineBlock =
  | 'not_open_lane' // 400
  | 'rerun' // 400
  | 'no_sender' // 400
  | 'no_inbound_id' // 400
  | 'wrong_status' // 409
  | 'already_declined' // 409
  | 'has_analysis' // 409
  | 'recipient_refused' // 409 "Decline not sent: recipient address refused" / "no-reply / automated sender" / "internal / team address"
  | 'not_dmarc_aligned' // 409 "Decline not sent: inbound not DMARC-aligned"
  | 'loop_guard'; // 409 "Decline not sent: loop guard: …"

const isFilled = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '';

type PlanFacts = { open_lane_outcome?: unknown; properties?: unknown };

/**
 * "No property found" as the server defines it: the plan's outcome says so, or its properties array is
 * present and empty. A job with no plan at all is NOT an empty plan.
 */
function noPropertyFound(plan: PlanFacts): boolean {
  return plan.open_lane_outcome === 'no_property' || (Array.isArray(plan.properties) && plan.properties.length === 0);
}

/**
 * The first reason the server would refuse Send decline for this job, or null when it would send. Copies
 * decideStaffDecline's job checks (report-send.ts), then what the send itself refuses on before any lookup, in its
 * order (external-ack.ts preflightRefusal): the recipient, then the header facts: ai-import-jobs' ack_allowed is
 * dmarc_pass and ack_suppression_reason is loop_suppression_reason, which must be exactly null. The session
 * (401, 403) is the server's to judge. The guards that need a lookup are not here: a decline already sent for this
 * inbound message is declineAlreadySent's (the page reads the lead and the job's mail), and the per-sender cap
 * surfaces as the server's error text.
 */
export function openLaneDeclineBlock(job: DeclineJob, plan: unknown): DeclineBlock | null {
  if (job.intake_lane !== 'open') return 'not_open_lane';
  if (job.parent_job_id) return 'rerun';
  if (!isFilled(job.source_email_sender)) return 'no_sender';
  if (!isFilled(job.source_email_message_id)) return 'no_inbound_id';
  if (job.status !== 'completed_pending_review') return 'wrong_status';
  const facts = (plan ?? {}) as PlanFacts;
  if (facts.open_lane_outcome === 'declined_by_staff') return 'already_declined';
  if (!noPropertyFound(facts)) return 'has_analysis';
  if (recipientRefusal(String(job.source_email_sender).trim())) return 'recipient_refused';
  if (job.ack_allowed !== true) return 'not_dmarc_aligned';
  if (job.ack_suppression_reason !== null) return 'loop_guard';
  return null;
}

/** Does the server accept Send decline for this job? When false, the page draws no button. */
export function canSendOpenLaneDecline(job: DeclineJob, plan: unknown): boolean {
  return openLaneDeclineBlock(job, plan) === null;
}

/**
 * Why there is no Send decline button, said only when the sender's address or inbound mail is the one thing in the way: the
 * teammate who clicked the "No property found" email's button would otherwise land on a page with no
 * explanation. Any other reason has its own sign on the page (the badge, the re-run link, the status).
 */
export function declineBlockedNote(job: DeclineJob, plan: unknown): string | null {
  const block = openLaneDeclineBlock(job, plan);
  if (block === 'recipient_refused') {
    const why = recipientRefusal(String(job.source_email_sender).trim());
    return `A decline can't be sent: ${
      why === 'internal'
        ? "the sender's address is one of our own"
        : why === 'automated'
          ? "the sender's address looks like a no-reply or automated one, and Flow does not answer those"
          : "the sender's address is not a plain email address"
    }.`;
  }
  if (block === 'not_dmarc_aligned') {
    return "A decline can't be sent: the sender's email did not pass DMARC alignment, and Flow answers only aligned mail.";
  }
  if (block === 'loop_guard') {
    return `A decline can't be sent: ${ackSuppressionText(job.ack_suppression_reason) ?? "the email's headers could not be checked"}.`;
  }
  return null;
}

/** What the page says, in place of the button, once a decline has gone to this email's sender. */
export const DECLINE_ALREADY_SENT_NOTE = 'A decline was already sent for this email.';

/**
 * Has a decline already gone to this job's sender? The server sends one per inbound message and answers a second
 * with a 409 (external-ack.ts reads the lead's decline_sent_at and the job's mail), so the page must not offer it.
 * Two records say so: the inbound_leads row of the message (`decline_sent_at`, stamped by the automatic decline
 * too, which is how "Treat as property" on an auto-declined email later finds itself refused), and an outbound
 * `decline` in the job's own email history (a staff decline whose outcome never landed on the plan).
 */
export function declineAlreadySent(args: {
  leads?: ReadonlyArray<Pick<OpenLaneLead, 'decline_sent_at'>> | null;
  emails?: ReadonlyArray<Pick<AIImportEmailMessage, 'kind' | 'direction'>> | null;
}): boolean {
  return (
    (args.leads ?? []).some((lead) => !!lead.decline_sent_at) ||
    (args.emails ?? []).some((mail) => mail.kind === 'decline' && mail.direction === 'outbound')
  );
}

/**
 * A re-run cannot send the decline (400): it is sent from the original submission. This is the id of that
 * original when the re-run is otherwise in the same place, an open-lane job that finished with no property
 * found, so the page can point there; null for everything else.
 */
export function declineFromOriginalId(job: DeclineJob, plan: unknown): string | null {
  if (job.intake_lane !== 'open' || !job.parent_job_id) return null;
  if (job.status !== 'completed_pending_review') return null;
  return noPropertyFound((plan ?? {}) as PlanFacts) ? job.parent_job_id : null;
}

/**
 * `?action=decline`, the link the internal "No property found" email carries. It is handled once the job has
 * loaded: `open` says whether to show the confirm modal (only when the decline is available and none has gone
 * already; the link never sends anything itself), and `strip` says to remove the param, either way, so a refresh
 * does not reopen it. `pending` is the page still reading whether a decline already went (the lead): it waits, and
 * decides when the answer is in. Any other action, or none, is not this link's business.
 */
export function declineDeepLink(args: {
  action: string | null;
  job: DeclineJob | null | undefined;
  plan: unknown;
  pending?: boolean;
  alreadyDeclined?: boolean;
}): { open: boolean; strip: boolean } {
  if (args.action !== 'decline' || !args.job || args.pending) return { open: false, strip: false };
  return { open: canSendOpenLaneDecline(args.job, args.plan) && !args.alreadyDeclined, strip: true };
}

// ---------------------------------------------------------------------------------------------
// Filing
// ---------------------------------------------------------------------------------------------

/** What the filing rules read off a job file (FileableJobFile in _shared/ai-import-filing.ts). */
export interface FilingFileFacts {
  id: string;
  apply_property_id: string | null;
  sharepoint_item_id?: string | null;
  sharepoint_drive_id?: string | null;
  user_skip?: boolean | null;
  staging_overwritten?: boolean | null;
}

/** What they read off a filing row. AIImportJobFileFiling fits. */
export interface FilingRowFacts {
  job_file_id: string;
  property_id: string;
  state: AIImportJobFileFiling['state'];
  error: string | null;
  filing_started_at?: string | null;
  sharepoint_web_url?: string | null;
}

/**
 * Would ai-import-file file this job file at all? Only a SharePoint item can be copied inside SharePoint (a
 * Storage-backed upload, seller financials, is filed by financials-file); a file the reviewer skipped, or
 * whose staging bytes a later email replaced, is never copied; and it needs a property to go under. This is
 * isFileable plus the target property filingQueueRows groups by.
 */
export function isFileableJobFile(file: FilingFileFacts): boolean {
  return (
    !!file.sharepoint_item_id &&
    !!file.sharepoint_drive_id &&
    !file.user_skip &&
    !file.staging_overwritten &&
    !!file.apply_property_id
  );
}

/** A filing claim is live for this long; after it the filer is presumed dead and a drain re-claims the row. */
const FILING_CLAIM_STALE_MS = 15 * 60_000;

/** A manual Retry re-queues every failed row except these: nothing could ever file them (isRequeueable). */
const NEVER_REQUEUE: ReadonlySet<string> = new Set(['no_sharepoint_source', 'staging_overwritten', 'job_cancelled']);

/**
 * Failures a manual Retry does put back in the queue, and the drain then fails again for the very reason the row
 * already has: the file was skipped, or the property is gone (fileRow / folderVerdict in ai-import-file). Retrying
 * only those changes nothing, so it is not a reason to offer the button. Together with NEVER_REQUEUE these are
 * exactly the server's terminal errors (TERMINAL_FILING_ERRORS), which the tests hold them to.
 */
const REFAILS_IDENTICALLY: ReadonlySet<string> = new Set(['user_skipped', 'property_missing', 'property_deleted']);

/**
 * Does `retry` change anything about this existing row? A pending row is drained. A failed row is put back to
 * pending, unless nothing could ever file it, or the drain would only fail it again the way it failed. A filing
 * row is re-claimed only once its claim is stale (no stamp at all counts as stale, as in the drain's own
 * query). Filed and unfiled rows are never touched.
 */
function retryTouches(row: FilingRowFacts, nowMs: number): boolean {
  switch (row.state) {
    case 'pending':
      return true;
    case 'failed':
      return !NEVER_REQUEUE.has(row.error ?? '') && !REFAILS_IDENTICALLY.has(row.error ?? '');
    case 'filing': {
      const started = row.filing_started_at ? Date.parse(row.filing_started_at) : NaN;
      return !Number.isFinite(started) || nowMs - started >= FILING_CLAIM_STALE_MS;
    }
    default:
      return false;
  }
}

/**
 * Should the Files card offer Retry filing? Only for an applied job that has at least ONE filing row, and only
 * when `retry` would change something: queue a row a fileable file never got (apply queued some rows and not
 * all), put a failed row back, or drain a queued or abandoned one. Never for a file filed elsewhere or never
 * filed (Storage-backed, skipped, overwritten, no property), never for a failure nothing could re-file, and
 * never when every failed row is one the drain would only fail again the same way (a skipped file, a property
 * that is gone) and nothing else would be queued or claimed. Rows are read per (file, property): a copy filed
 * under some other property does not settle this one.
 *
 * A job with NO rows at all is the one place this offers less than the server would do: every email job applied
 * before the filing queue existed has none, its files look "never queued", and a click would ask a function that
 * may not be deployed yet to file a job it has never seen. Filing those is the backfill's work, not a button's,
 * so a job with no rows shows no filing UI at all. `filings` must be the loaded rows: an empty list read as
 * "nothing filed" is that same no-rows answer, so the button never shows while they load either.
 */
export function retryFilingAvailable(
  job: Pick<AIImportJob, 'status'>,
  files: readonly FilingFileFacts[],
  filings: readonly FilingRowFacts[],
  nowMs: number = Date.now(),
): boolean {
  if (job.status !== 'applied') return false;
  if (filings.length === 0) return false;
  if (filings.some((row) => retryTouches(row, nowMs))) return true;
  return files.some(
    (file) =>
      isFileableJobFile(file) &&
      !filings.some((row) => row.job_file_id === file.id && row.property_id === file.apply_property_id),
  );
}

export interface FileFilingView {
  /** A copy is filed under some property. */
  filed: boolean;
  /** Its link: the first filed copy whose URL is a usable https URL. Null shows the words without a link. */
  filedUrl: string | null;
  /** A filing failed (the first one's error, cleaned; null when it left none). */
  failed: boolean;
  failedError: string | null;
  /** A row is still queued or being filed. */
  inFlight: boolean;
}

/** One file's filing line. A file filed under one park can still be failing or queued under another. */
export function fileFilingView(file: Pick<FilingFileFacts, 'id'>, filings: readonly FilingRowFacts[]): FileFilingView {
  const rows = filings.filter((row) => row.job_file_id === file.id);
  const filed = rows.filter((row) => row.state === 'filed');
  const failed = rows.find((row) => row.state === 'failed');
  let filedUrl: string | null = null;
  for (const row of filed) {
    filedUrl = safeExternalUrl(row.sharepoint_web_url);
    if (filedUrl) break;
  }
  return {
    filed: filed.length > 0,
    filedUrl,
    failed: !!failed,
    failedError: failed ? cleanDisplayText(failed.error) || null : null,
    inFlight: rows.some((row) => row.state === 'pending' || row.state === 'filing'),
  };
}

export interface FilingRetryResult {
  /** ai-import-file's own verdict on the run (`failures` empty); null when the answer did not carry one. */
  ok: boolean | null;
  filed: number;
  failed: number;
  /** Rows the call did not reach in its time budget: they stay queued. */
  deferred: number;
  /** What went wrong beyond a file failing: a property whose folder could not be looked up, a job that was gone. */
  failures: string[];
}

const wholeCount = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);

/** ai-import-file's answer to `retry`, read defensively: a missing or malformed number is zero, never NaN. */
export function readRetryResponse(data: unknown): FilingRetryResult {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  return {
    ok: typeof d.ok === 'boolean' ? d.ok : null,
    filed: wholeCount(d.filed),
    failed: wholeCount(d.failed),
    deferred: wholeCount(d.deferred),
    failures: Array.isArray(d.failures) ? d.failures.filter((f): f is string => typeof f === 'string') : [],
  };
}

/**
 * The one line a retry's toast says: "3 filed, 1 failed, 2 still queued". A run that reports problems of its own
 * (`failures`, or `ok: false` with none listed) says so, "2 problems", and is a warning: a call that returned
 * 200 can still have left something unfiled that the counts alone do not show.
 */
export function describeRetryResult(result: FilingRetryResult): { tone: 'success' | 'warning'; message: string } {
  const problems = Math.max(result.failures.length, result.ok === false ? 1 : 0);
  if (!result.filed && !result.failed && !result.deferred && problems === 0) {
    return { tone: 'success', message: 'Nothing was waiting to be filed.' };
  }
  const parts = [`${result.filed} filed`];
  if (result.failed) parts.push(`${result.failed} failed`);
  if (result.deferred) parts.push(`${result.deferred} still queued`);
  if (problems) parts.push(`${problems} problem${problems === 1 ? '' : 's'}`);
  return { tone: result.failed > 0 || problems > 0 ? 'warning' : 'success', message: parts.join(', ') };
}

// ---------------------------------------------------------------------------------------------
// The open-lane activity rows
// ---------------------------------------------------------------------------------------------

/**
 * The tiers the poller's override pass picks up (runOpenLaneOverrides: declined, capped, dropped, and the
 * in-flight pending it retries). "Treat as property" is offered on, and only writes for, the first three.
 */
export const OVERRIDABLE_LEAD_TIERS = ['open_declined', 'open_capped', 'open_dropped'] as const;

type LeadFacts = Pick<OpenLaneLead, 'lead_tier' | 'override_requested_at' | 'triage' | 'header_facts'>;

/** What the poller's override pass reads from inbound_settings (tier2Route: accept_unknown_senders, open_lane_mode). */
export interface OverrideSettings {
  accept_unknown_senders?: unknown;
  open_lane_mode?: unknown;
}

export type OverrideRuns =
  | { runs: true }
  | {
      runs: false;
      /** Why "Treat as property" is disabled, for the button's title. */
      title: string;
      /** What a request that is already queued says while it waits. */
      queuedText: string;
    };

const OVERRIDES_RUN: OverrideRuns = { runs: true };

/**
 * Will a "Treat as property" request be picked up? The poller runs its override pass only when tier2Route is
 * 'analyze': unknown senders accepted AND the open lane in "Triage and analyze" mode. In "Create a lead only"
 * mode, or with the lane off, a request would sit there unread, so the button says so instead.
 *
 * `settings` is the inbound_settings row, which any signed-in staff member may read (flow_allowed_select,
 * migration 146). When it could not be read (still loading, an error, no row, a shape that is not a row) nothing
 * is claimed either way: `runs` is true, the button is as it always was, and no error is drawn.
 */
export function overrideRuns(settings: OverrideSettings | null | undefined): OverrideRuns {
  if (!settings || typeof settings !== 'object') return OVERRIDES_RUN;
  const { accept_unknown_senders: accepts, open_lane_mode: mode } = settings;
  if (accepts === false) {
    return {
      runs: false,
      title:
        'Overrides run only while the open lane is on, in "Triage and analyze" mode. Accepting mail from unknown senders is off now; an admin can change it in Admin Center > Inbound Email.',
      queuedText: 'queued — runs when the open lane is on, in analyze mode',
    };
  }
  if (typeof mode === 'string' && mode !== 'analyze') {
    return {
      runs: false,
      title:
        'Overrides run only in "Triage and analyze" mode. The open lane is in "Create a lead only" mode now; an admin can change it in Admin Center > Inbound Email.',
      queuedText: 'queued — runs when the open lane is in analyze mode',
    };
  }
  return OVERRIDES_RUN;
}

/** "Treat as property" shows on a declined, capped or dropped lead until it has been requested. */
export function canTreatAsProperty(lead: Pick<LeadFacts, 'lead_tier' | 'override_requested_at'>): boolean {
  return (OVERRIDABLE_LEAD_TIERS as readonly string[]).includes(lead.lead_tier) && !lead.override_requested_at;
}

// What the poller writes to triage.drop_reason on an open_dropped lead (lib/openLane.ts, openLaneFlow.ts), in
// words. A code it does not know is shown as it was written.
const DROP_REASON_WORDS = new Map<string, string>([
  ['submit_refused', 'The submission was refused'],
  ['no_content', 'Nothing to analyze'],
  ['all_uploads_failed', 'Every upload failed'],
  ['retries_exhausted', 'Retries ran out'],
  ['override_failed', 'The override failed'],
  ['open_lane_off', 'The open lane was switched off'],
  ['open_lane_lead_mode', 'The open lane was in "Create a lead only" mode'],
  ['no_sender_address', 'The email had no sender address'],
  ['sender_address_too_long', "The sender's address was too long to be real"],
  ['sender_address_wildcard', "The sender's address contained a wildcard (*)"],
]);

const nonBlankText = (v: unknown): string | null => (typeof v === 'string' ? cleanDisplayText(v) || null : null);

/**
 * Why a lead row is where it is, from its triage jsonb, which can hold anything. The poller keeps the reason
 * beside the model's verdict, under its own key: a capped lead says which cap (cap_reason: global hourly,
 * per-sender daily, daily job), a dropped one why the intake stopped (drop_reason, one of DROP_REASON_WORDS'
 * codes; one it does not know is shown as written) and, for a failed override, the failure itself
 * (override_failure, the last failure's text). None of those present: the verdict's own reason (triage.reason,
 * "open lane off" on a row made while the lane was off), which is all a declined or older row has.
 */
export function leadReason(lead: Pick<LeadFacts, 'triage'>): string | null {
  const triage = lead.triage as unknown;
  if (!triage || typeof triage !== 'object' || Array.isArray(triage)) return null;
  const t = triage as Record<string, unknown>;
  const cap = nonBlankText(t.cap_reason);
  const drop = nonBlankText(t.drop_reason);
  const failure = nonBlankText(t.override_failure);
  const parts: string[] = [];
  if (cap) parts.push(`Held by the ${cap} cap`);
  if (drop) {
    const words = DROP_REASON_WORDS.get(drop) ?? drop;
    parts.push(failure ? `${words}: ${failure}` : words);
  } else if (failure) {
    parts.push(failure);
  }
  return parts.length ? parts.join(' · ') : nonBlankText(t.reason);
}

/** Why no reply went to this sender, from the header facts the poller recorded at intake. */
export function leadNoReplyNote(lead: Pick<LeadFacts, 'header_facts'>): string | null {
  const facts = lead.header_facts as unknown;
  if (!facts || typeof facts !== 'object') return null;
  const { dmarc_pass: dmarc, loop_suppression_reason: loop } = facts as { dmarc_pass?: unknown; loop_suppression_reason?: unknown };
  if (dmarc === false) return 'Not DMARC-aligned — no reply was sent';
  if (typeof loop === 'string') {
    const why = ackSuppressionText(loop);
    if (why) return `No reply was sent — ${why}`;
  }
  return null;
}
